/**
 * LobbyScene — salas online por código (§12.4/§16).
 *
 * Criar sala mostra o código de 5 letras e o elenco; entrar digita o código
 * no teclado (alfabeto sem ambiguidade). Só o host inicia (§12.4); todos
 * recebem `{k:'begin', seed}` e largam juntos com countdown local de 3 s
 * (§12.3 — a sincronia fina por relógio do servidor é refinamento do M8).
 *
 * DOIS modos, escolhidos no menu e recebidos por `init`: CORRIDA (§12.3 —
 * mundos paralelos, cada um no seu rio, o outro aparece como fantasma) e
 * CO-OP (§12.2 — um rio só, vidas do time, o slot 0 é a autoridade). A sala
 * e o fluxo de código são idênticos; o que muda é o que a largada monta.
 */

import Phaser from 'phaser'
import { NET, SCREEN } from '@pixelraid/core'
import { onKey } from '../input/keys'
import { NetLink } from '../net/NetLink'
import { TouchButton, isTouchDevice } from '../ui/buttons'
import { slotColor, SLOT_ROLE } from '../ui/players'
import { pixelText, pixelTitle, preloadPixelFonts, setPixelText } from '../ui/text'
import type { CoopConfig, RaceConfig } from './GameScene'


declare global {
  interface Window {
    __pixelraidLobby?: { code: string | null; peers: number; stage: string }
  }
}

type Stage = 'connecting' | 'choice' | 'typing' | 'waiting' | 'error'

export class LobbyScene extends Phaser.Scene {
  private link!: NetLink
  private stage: Stage = 'connecting'
  /** Corrida (mundos paralelos) ou co-op (mundo compartilhado). Vem do menu. */
  private mode: 'race' | 'coop' = 'race'
  private typed = ''
  private statusText!: Phaser.GameObjects.BitmapText
  private codeText!: Phaser.GameObjects.BitmapText
  private hintText!: Phaser.GameObjects.BitmapText
  /**
   * Uma linha POR jogador, e não um texto multi-linha: `BitmapText` tem um
   * tint só, então a lista inteira sairia da mesma cor — que é justamente o
   * que a identidade visual precisa desfazer.
   */
  private rosterLines: Phaser.GameObjects.BitmapText[] = []
  /** Botões do estágio atual; vazio em aparelho sem toque. */
  private botoes: TouchButton[] = []
  /** Este aparelho é de dedo? Decidido uma vez, no create. */
  private touch = false

  constructor() {
    super({ key: 'lobby', active: false })
  }

  preload(): void {
    this.load.setBaseURL(import.meta.env.BASE_URL)
    preloadPixelFonts(this)
  }

  init(data: { mode?: 'race' | 'coop' }): void {
    this.mode = data.mode ?? 'race'
  }

  create(): void {
    const centerX = SCREEN.WIDTH / 2
    pixelTitle(
      this,
      centerX,
      46,
      16,
      this.mode === 'coop' ? 'CO-OP ONLINE' : 'CORRIDA ONLINE',
      '#f2f2f2',
      '#ff8c1a',
    )

    this.statusText = pixelText(this, centerX, 100, 12, 'conectando...', {
      color: '#9ae6b4',
      align: 'center',
      origin: [0.5, 0.5],
    })
    this.codeText = pixelText(this, centerX, 150, 32, '', {
      color: '#ffd23c',
      letterSpacing: 6,
      origin: [0.5, 0.5],
    })
    // Cenas do Phaser são instância ÚNICA: sem zerar aqui, o segundo acesso ao
    // lobby herdaria referências a objetos já destruídos (armadilha registrada
    // no CLAUDE.md). O `create` corre depois do `init`, então é aqui mesmo.
    this.rosterLines = []
    this.botoes = []
    this.touch = isTouchDevice(this)
    this.hintText = pixelText(this, centerX, 328, 10, '', {
      color: '#7a8a99',
      align: 'center',
      origin: [0.5, 0.5],
    })

    this.stage = 'connecting'
    this.link = new NetLink()
    this.link.onPeerJoined = () => this.refreshRoster()
    this.link.onPeerLeft = (_id, _slot, wasHost) => {
      if (wasHost) this.fail('o anfitrião saiu — sala encerrada')
      else this.refreshRoster()
    }
    this.link.onClose = () => {
      if (this.stage !== 'error') this.fail('conexão perdida')
    }
    this.link.onMessage = (_from, d) => {
      if (d.k === 'begin') this.launchRace(Number(d.seed))
    }

    this.link
      .connect()
      .then(() => {
        this.stage = 'choice'
        // Duas linhas por aritmética, não por gosto: numa linha só isto mede
        // 264px numa tela de 256 e, centralizado, some 4px em CADA ponta — o
        // "C" de "criar" e o "r" de "voltar" saíam cortados.
        setPixelText(
          this.statusText,
          this.touch
            ? 'crie uma sala ou entre com o código'
            : 'C criar sala · J entrar com código\nESC voltar',
        )
        this.montarToque()
        this.syncHook()
      })
      .catch(() => this.fail('servidor offline — tente mais tarde'))

    onKey(this.input.keyboard!, 'keydown', (event) => this.onKey(event))
  }

  private onKey(event: KeyboardEvent): void {
    const key = event.key.toUpperCase()
    if (key === 'ESCAPE') {
      this.sair()
      return
    }

    if (this.stage === 'choice') {
      if (key === 'C') void this.createRoom()
      if (key === 'J') this.pedirCodigo()
      return
    }

    if (this.stage === 'typing') {
      if (key === 'BACKSPACE') this.apagar()
      else if (key.length === 1 && NET.ROOM_CODE_ALPHABET.includes(key)) this.digitar(key)
      return
    }

    if (this.stage === 'waiting' && key === 'ENTER') this.tentarComecar()
  }

  // ── Transições, cada uma num lugar só ─────────────────────────────────────
  //
  // Teclado e dedo chamam EXATAMENTE os mesmos métodos. Antes as transições
  // moravam dentro do `onKey`, o que tornava o lobby inalcançável sem teclado
  // — e num celular não há tecla C, J, ENTER nem ESC, então criar ou entrar
  // numa sala era simplesmente impossível.

  private sair(): void {
    this.link.close()
    this.scene.start('menu')
  }

  private pedirCodigo(): void {
    this.stage = 'typing'
    this.typed = ''
    setPixelText(this.statusText, 'digite o código de 5 letras')
    setPixelText(this.codeText, '_____')
    this.montarToque()
    // O gancho tem de acompanhar TODA troca de estágio. Faltava aqui desde
    // antes, e não incomodava enquanto só o teclado dirigia o lobby; o teste
    // de toque viu na hora, porque ele observa o estado por este gancho.
    this.syncHook()
  }

  private digitar(ch: string): void {
    this.typed = (this.typed + ch).slice(0, NET.ROOM_CODE_LENGTH)
    setPixelText(this.codeText, this.typed.padEnd(NET.ROOM_CODE_LENGTH, '_'))
    if (this.typed.length === NET.ROOM_CODE_LENGTH) void this.joinRoom(this.typed)
  }

  private apagar(): void {
    this.typed = this.typed.slice(0, -1)
    setPixelText(this.codeText, this.typed.padEnd(NET.ROOM_CODE_LENGTH, '_'))
  }

  private tentarComecar(): void {
    if (this.link.room?.slot !== 0) return
    // Só o host inicia (§12.4), com 2+ na sala.
    if ((this.link.room?.peers.length ?? 0) >= 2) this.beginRace()
    else setPixelText(this.statusText, 'esperando pelo menos 1 convidado...')
  }

  private async createRoom(): Promise<void> {
    try {
      const room = await this.link.createRoom(this.mode, 'P1')
      this.stage = 'waiting'
      setPixelText(this.codeText, room.code)
      setPixelText(
        this.statusText,
        this.touch ? 'passe o código a quem for jogar' : 'passe o código; ENTER inicia (host)',
      )
      this.montarToque()
      this.refreshRoster()
    } catch (error) {
      this.fail(String(error instanceof Error ? error.message : error))
    }
  }

  private async joinRoom(code: string): Promise<void> {
    try {
      const room = await this.link.joinRoom(code, `P${'?'}`)
      this.stage = 'waiting'
      setPixelText(this.codeText, room.code)
      setPixelText(this.statusText, 'aguardando o anfitrião iniciar...')
      this.montarToque()
      this.refreshRoster()
    } catch (error) {
      const reason = error instanceof Error ? error.message : String(error)
      this.stage = 'typing'
      this.typed = ''
      setPixelText(this.codeText, '_____')
      setPixelText(
        this.statusText,
        reason === 'notfound' ? 'sala não existe — tente de novo' : `erro: ${reason}`,
      )
      this.montarToque()
    }
  }

  /**
   * (Re)monta os botões do estágio atual.
   *
   * Reconstrói do zero a cada transição em vez de esconder/mostrar: são
   * poucos objetos, criados em troca de tela e não por frame, e o custo disso
   * é muito menor que o de manter visibilidade de quatro conjuntos em sincronia
   * — que é o tipo de estado que sai de compasso justo no caso raro.
   */
  private montarToque(): void {
    for (const b of this.botoes) b.destroy()
    this.botoes = []
    if (!this.touch) return

    const meio = SCREEN.WIDTH / 2
    const largo = 200
    const alto = 30

    if (this.stage === 'choice') {
      this.botoes.push(
        new TouchButton(this, meio, 178, largo, alto, 'CRIAR SALA', () => void this.createRoom()),
        new TouchButton(this, meio, 216, largo, alto, 'ENTRAR COM CÓDIGO', () => this.pedirCodigo()),
        new TouchButton(this, meio, 262, largo, alto, 'VOLTAR', () => this.sair(), {
          fill: null,
          color: '#7a8a99',
        }),
      )
      return
    }

    if (this.stage === 'typing') {
      // Teclado do alfabeto de sala: 24 teclas em 6 colunas. Não é o teclado
      // do sistema de propósito — ele cobriria metade da tela, traria
      // autocorreção e letras que o código nem aceita.
      const alfabeto = NET.ROOM_CODE_ALPHABET
      const colunas = 6
      const passoX = 36
      const passoY = 27
      const x0 = meio - ((colunas - 1) * passoX) / 2
      const y0 = 196
      for (const [i, ch] of [...alfabeto].entries()) {
        const cx = x0 + (i % colunas) * passoX
        const cy = y0 + Math.floor(i / colunas) * passoY
        this.botoes.push(
          new TouchButton(this, cx, cy, passoX - 3, passoY - 3, ch, () => this.digitar(ch), {
            size: 12,
          }),
        )
      }
      const abaixo = y0 + Math.ceil(alfabeto.length / colunas) * passoY + 6
      this.botoes.push(
        new TouchButton(this, meio - 54, abaixo, 100, 28, 'APAGAR', () => this.apagar(), {
          color: '#ffd23c',
        }),
        new TouchButton(this, meio + 54, abaixo, 100, 28, 'VOLTAR', () => this.sair(), {
          fill: null,
          color: '#7a8a99',
        }),
      )
      return
    }

    if (this.stage === 'waiting') {
      if (this.link.room?.slot === 0) {
        this.botoes.push(
          new TouchButton(this, meio, 306, largo, alto, 'COMEÇAR', () => this.tentarComecar(), {
            color: '#9ae6b4',
          }),
        )
      }
      this.botoes.push(
        new TouchButton(this, meio, 348, largo, 26, 'SAIR DA SALA', () => this.sair(), {
          fill: null,
          color: '#7a8a99',
          size: 10,
        }),
      )
      return
    }

    if (this.stage === 'error') {
      this.botoes.push(new TouchButton(this, meio, 300, largo, alto, 'VOLTAR', () => this.sair()))
    }
  }

  /** Gancho de teste: Text do Phaser vive em canvas, invisível ao DOM. */
  private syncHook(): void {
    window.__pixelraidLobby = {
      code: this.link.room?.code ?? null,
      peers: this.link.room?.peers.length ?? 0,
      stage: this.stage,
    }
  }

  private refreshRoster(): void {
    this.syncHook()
    const room = this.link.room
    if (room === null) return
    // Identidade visual: cada jogador na SUA cor, já aqui no lobby, para que a
    // associação "eu sou o verde" esteja feita antes da largada — no meio do
    // rio, com três aviões na tela, é tarde para descobrir qual é o seu.
    const peers = [...room.peers].sort((a, b) => a.slot - b.slot)
    while (this.rosterLines.length < peers.length) {
      this.rosterLines.push(
        pixelText(this, SCREEN.WIDTH / 2, 206 + this.rosterLines.length * 16, 12, '', {
          align: 'center',
          origin: [0.5, 0.5],
        }),
      )
    }
    for (const [i, linha] of this.rosterLines.entries()) {
      const peer = peers[i]
      linha.setVisible(peer !== undefined)
      if (peer === undefined) continue
      setPixelText(linha, `P${peer.slot + 1} ${SLOT_ROLE[peer.slot] ?? ''}${peer.peerId === room.peerId ? ' (você)' : ''}`)
      linha.setTint(slotColor(peer.slot))
    }
    setPixelText(
      this.hintText,
      this.mode === 'coop'
        ? room.slot === 0
          ? 'mesmo rio, vidas do TIME\nENTER começa'
          : 'mesmo rio, vidas do TIME'
        : room.slot === 0
          ? // Sem "§12.3": citação de spec não diz nada a jogador, e o "§" nem
            // tem glifo no atlas — sumia calado, deixando um "(12.3)" órfão.
            'mesma seed para todos\nvence a maior distância\n1 vida — sem respawn'
          : 'mesma seed para todos\nvence a maior distância',
    )
  }

  /** Host: sorteia a seed e avisa todo mundo (relay cego: begin é só `d`). */
  private beginRace(): void {
    const seed = (Math.random() * 0xffffffff) >>> 0
    this.link.send({ k: 'begin', seed })
    this.launchRace(seed)
  }

  private launchRace(seed: number): void {
    const room = this.link.room
    if (room === null) return
    if (this.mode === 'coop') {
      // Mundo compartilhado: o slot 0 é a autoridade (§13.1), e todos
      // simulam os mesmos N aviões — não há fantasma aqui.
      const coop: CoopConfig = {
        link: this.link,
        mySlot: room.slot,
        peers: room.peers,
        isHost: room.slot === 0,
      }
      this.scene.start('game', { seed, daily: null, coop })
      return
    }
    const race: RaceConfig = {
      link: this.link,
      myPeerId: room.peerId,
      mySlot: room.slot,
      peers: room.peers,
    }
    this.scene.start('game', { seed, daily: null, race })
  }

  private fail(reason: string): void {
    this.stage = 'error'
    this.syncHook()
    setPixelText(this.statusText, reason)
    setPixelText(this.codeText, '')
    // Sem o botão, um erro no celular era beco sem saída: a única saída
    // anunciada era uma tecla que o aparelho não tem.
    setPixelText(this.hintText, this.touch ? '' : 'ESC volta ao menu')
    this.montarToque()
  }
}
